/**
 * 일손이 관리자 화면 (기반 공사 6단계, one_brain/공통/보안과개인정보 1-4-3)
 *
 * - 소장님 화면과 주소(/admin)도, 출입증도 따로다. 검색에 나오지 않게 한다(noindex).
 * - 휴대폰에서도 편하게 보이게 짠다(대표님 "UIUX만 그렇게 짜 놓으면 될 듯"). 디자인은 단순하게.
 * - 사용 기록은 남기지 않는다(관리자 화면은 소장님 사용 현황에 섞이지 않게).
 * 화면: 로그인(아이디와 비밀번호 → 텔레그램 인증번호), 처음 설정, 기기 확인(비상 길), 사무소 목록, 사무소 하나, 사용 현황, 텔레그램.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api.ts';
import { Button, Check, Countdown, Field, Message } from '../ui.tsx';
import { AlertsPage, OfficePage, OfficesPage, UsagePage } from './AdminPages.tsx';
import { adminStyle, wrap } from './style.ts';

function useNoIndex(): void {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    document.title = '일손이 관리자';
    return () => meta.remove();
  }, []);
}

function query(name: string): string {
  return new URLSearchParams(window.location.search).get(name) ?? '';
}

function Login({ onDone }: { onDone: () => void }) {
  const [login_id, setId] = useState('');
  const [password, setPw] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<{ code_id: string; seconds: number; at: number; test_code?: string } | null>(null);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const first = async () => {
    setBusy(true);
    setError(null);
    const r = await api<{ done: boolean; code_id?: string; seconds?: number; message?: string; test_code?: string }>('POST', '/api/admin/login', { login_id, password });
    setBusy(false);
    if (!r.ok) return setError(r.message);
    if (r.done) return onDone();
    setStep({ code_id: r.code_id ?? '', seconds: r.seconds ?? 600, at: Date.now(), ...(r.test_code ? { test_code: r.test_code } : {}) });
    setInfo(r.message ?? null);
  };

  const second = async () => {
    if (!step) return;
    setBusy(true);
    setError(null);
    const r = await api('POST', '/api/admin/login/code', { code_id: step.code_id, code, remember });
    setBusy(false);
    if (!r.ok) {
      setError(r.message);
      if (r.code === 'code_retry' || r.code === 'code_expired') setStep(null);
      return;
    }
    onDone();
  };

  return (
    <main style={wrap}>
      <h1>일손이 관리자</h1>
      {!step ? (
        <>
          <Field label="아이디" value={login_id} onChange={setId} autoComplete="username" />
          <Field label="비밀번호" type="password" value={password} onChange={setPw} autoComplete="current-password" />
          <Message text={error} />
          <Button primary disabled={busy || !login_id || !password} onClick={() => void first()}>다음</Button>
        </>
      ) : (
        <>
          <Message text={info} kind="info" />
          <Countdown seconds={step.seconds} startedAt={step.at} onEnd={() => { setStep(null); setError('시간이 지났어요. 처음부터 다시 로그인해 주세요.'); }} />
          {step.test_code && <p>시험용 인증번호: {step.test_code}</p>}
          <Field label="인증번호" value={code} onChange={setCode} autoComplete="one-time-code" />
          <Check label="이 기기 30일 동안 기억하기" checked={remember} onChange={setRemember} />
          <Message text={error} />
          <Button primary disabled={busy || code.length < 6} onClick={() => void second()}>로그인</Button>
          <Button onClick={() => { setStep(null); setCode(''); setError(null); }}>처음으로</Button>
        </>
      )}
    </main>
  );
}

function Setup({ go }: { go: (p: string) => void }) {
  const t = query('t');
  const [ok, setOk] = useState<boolean | null>(null);
  const [login_id, setId] = useState('');
  const [password, setPw] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    void api('GET', `/api/admin/setup?t=${encodeURIComponent(t)}`).then((r) => {
      setOk(r.ok);
      if (!r.ok) setError(r.message);
    });
  }, [t]);

  const save = async () => {
    setError(null);
    if (password !== again) return setError('비밀번호가 서로 달라요. 다시 넣어 주세요.');
    const r = await api('POST', '/api/admin/setup', { t, login_id: login_id.trim().toLowerCase(), password });
    if (!r.ok) return setError(r.message);
    setDone(true);
  };

  return (
    <main style={wrap}>
      <h1>관리자 계정 설정</h1>
      {done ? (
        <>
          <Message text="설정했어요. 이제 로그인해 주세요." kind="info" />
          <Button primary onClick={() => go('/admin')}>로그인하러 가기</Button>
        </>
      ) : ok ? (
        <>
          <Field label="아이디 (영어 소문자와 숫자 6~20자)" value={login_id} onChange={setId} autoComplete="username" />
          <Field label="비밀번호 (8자 이상)" type="password" value={password} onChange={setPw} autoComplete="new-password" />
          <Field label="비밀번호 한 번 더" type="password" value={again} onChange={setAgain} autoComplete="new-password" />
          <Message text={error} />
          <Button primary disabled={!login_id || !password || !again} onClick={() => void save()}>설정하기</Button>
        </>
      ) : (
        <Message text={error} />
      )}
    </main>
  );
}

function Trust({ go }: { go: (p: string) => void }) {
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  useEffect(() => {
    void api('POST', '/api/admin/trust', { c: query('c'), k: query('k') }).then((r) =>
      setMsg(r.ok ? { text: '이 기기를 기억했어요. 아이디와 비밀번호로 로그인해 주세요.', ok: true } : { text: r.message, ok: false })
    );
  }, []);
  return (
    <main style={wrap}>
      <h1>기기 확인</h1>
      <Message text={msg?.text ?? null} kind={msg?.ok ? 'info' : 'error'} />
      {msg?.ok && <Button primary onClick={() => go('/admin')}>로그인하러 가기</Button>}
    </main>
  );
}

function Nav({ path, go, onLogout }: { path: string; go: (p: string) => void; onLogout: () => void }) {
  const items: [string, string][] = [['/admin/offices', '사무소'], ['/admin/usage', '사용 현황'], ['/admin/alerts', '텔레그램']];
  return (
    <nav className="admin-nav">
      {items.map(([p, label]) => (
        <a key={p} href={p} aria-current={path.startsWith(p) ? 'page' : undefined} onClick={(e) => { e.preventDefault(); go(p); }}>{label}</a>
      ))}
      <button type="button" onClick={onLogout}>로그아웃</button>
    </nav>
  );
}

export function AdminApp() {
  useNoIndex();
  const [path, setPath] = useState(window.location.pathname);
  const [me, setMe] = useState<string | null | undefined>(undefined);

  const go = useCallback((p: string) => {
    window.history.pushState(null, '', p);
    setPath(p.split('?')[0] ?? p);
    window.scrollTo(0, 0);
  }, []);

  const refresh = useCallback(async () => {
    const r = await api<{ login_id: string }>('GET', '/api/admin/me');
    setMe(r.ok ? r.login_id : null);
  }, []);

  useEffect(() => {
    const back = () => setPath(window.location.pathname);
    window.addEventListener('popstate', back);
    void refresh();
    return () => window.removeEventListener('popstate', back);
  }, [refresh]);

  /** 출입증이 끝났으면(30분) 로그인 화면으로 */
  const lost = useCallback(() => setMe(null), []);

  const body = (() => {
    if (path === '/admin/setup') return <Setup go={go} />;
    if (path === '/admin/trust') return <Trust go={go} />;
    if (me === undefined) return <main style={wrap}><p>잠시만 기다려 주세요.</p></main>;
    if (me === null) return <Login onDone={() => { void refresh(); go('/admin/offices'); }} />;
    const logout = async () => {
      await api('POST', '/api/admin/logout');
      setMe(null);
      go('/admin');
    };
    const office = /^\/admin\/offices\/([0-9a-f-]{36})$/.exec(path)?.[1];
    return (
      <>
        <Nav path={path} go={go} onLogout={() => void logout()} />
        <main style={wrap}>
          {office ? <OfficePage id={office} go={go} lost={lost} />
            : path === '/admin/usage' ? <UsagePage lost={lost} />
            : path === '/admin/alerts' ? <AlertsPage lost={lost} />
            : <OfficesPage go={go} lost={lost} />}
        </main>
      </>
    );
  })();

  return (
    <>
      <style>{adminStyle}</style>
      {body}
    </>
  );
}
